import { PropsWithChildren, useRef, useState, useEffect } from 'react';

import { store } from '@/data/libraries/Store';
import { useScreenSize, ScreenSize } from '@/ui/hooks/useScreenSize';

import { Col, Image, Modal } from 'react-bootstrap';

import { RootState } from '@/ui/store/store';
import { useAppSelector } from '@/ui/store/helperRedux';

import StoreController from '@/controllers/StoreController';
import { translate } from '@/domain/utils/translate';
import { IProduct } from '@/domain/interfaces/IProduct';
import { IAppointmentStore, IStore } from '@/domain/interfaces/IStore';
import { IRegistrationData, IUserStore } from '@/domain/interfaces/IUser';

import CloseIcon from '@/ui/assets/CloseIcon';

import UserDataForm from '@/ui/components/user/UserDataForm';
import TwoButtonsGroup from '@/ui/components/TwoButtonsGroup';
import InputForm from '@/ui/components/forms/InputForm';


interface PropsContentModal {
    /** Indica si el modal debe mostrarse. */
    showModal?: boolean;
    /** Título del modal */
    title?: string;
    /** Clase CSS para el título del modal */
    classTitle?: string;
    /** Clase CSS para el contenido del modal */
    classContent?: string;
    /** Indica si el modal está bloqueado */
    isBlocked?: boolean;
    /** Función para cerrar el modal */
    onClose?: () => void;
    /** Información del producto a mostrar en el modal */
    product?: IProduct;

    setSuccessAlertAppointment: (value: boolean) => void;
    setErrorAlertAppointment: (value: boolean) => void;
}

/**
 * Componente modal para reservar una cita.
 * 
 * @returns {JSX.Element} - Elemento JSX que representa el modal de reserva de cita.
 */
const BookAppointmentModal = ({
    showModal,
    title,
    classTitle,
    classContent,
    isBlocked,
    onClose = () => { },
    product,
    setSuccessAlertAppointment,
    setErrorAlertAppointment
}: PropsWithChildren<PropsContentModal>): JSX.Element => {

    const emptyUserData: IRegistrationData = {
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        phone: { number: '', country: '', },
        identificationId: { type: '', value: '', },
        idType: '',
        identification: '',
        error: false,
    };

    /** Referencia a los datos del usuario */
    const userData = useRef<IRegistrationData>(emptyUserData);

    /** Referencia al campo detalles */
    const detailsRef = useRef<HTMLInputElement | null>(null);

    /** State error del campo detalles */
    const [detailsInputWithError, setDetailsInputWithError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    /** Objeto Redux que almacena la data de la tienda */
    const storeData = useAppSelector((state: RootState) => state.storeData) ?? {} as IStore;
    /** Objeto Redux de usuario logueado */
    const userLogued: IUserStore = useAppSelector((state: RootState) => state.userState);

    /** Hook que contiene las dimensiones de la pantalla y maneja los componentes de acuerdo al resultado obtenido  */
    const { width, maxLargeWidth }: ScreenSize = useScreenSize();

    /** Controlador reservar cita */
    const { sendAppointment } = StoreController();

    /** Estado para habilitar/deshabilitar el botón */
    const [formIsValid, setFormIsValid] = useState<boolean>(false);

    /** Texto de la descripción del modal */
    const BOOK_APPOINTMENT_TEXT = translate('store.book_appointment_text');
    const MODAL_DETAILS_APPOINTMENT: string = translate('store.modal_details_appointment');

    /** Variable que define si es entorno mobile o desktop */
    const isMobile: boolean = width < maxLargeWidth;

    /**Función que actualiza el estado de los datos del cliente */
    const updateClientData = (newData: IRegistrationData) => {
        /** Se actualiza el estado con los nuevos datos */
        userData.current = newData;

        /** Validación básica de teléfono */
        const hasValidPhone = newData.phone &&
            newData.phone.number &&
            newData.phone.number.trim() !== '' &&
            newData.phone.country &&
            newData.phone.country.trim() !== '';

        /** Validar campos requeridos del usuario */
        const isUserDataValid =
            newData.firstName && newData.firstName.trim() !== '' &&
            newData.lastName && newData.lastName.trim() !== '' &&
            newData.email && newData.email.trim() !== '' &&
            hasValidPhone;

        /** Validar el campo de detalles */
        const isDetailsValid = detailsRef.current && detailsRef.current.value.trim().length > 0;

        if (isDetailsValid) {
            setErrorMessage('');
            setDetailsInputWithError(false);
        } else {
            setErrorMessage(translate('session.error_empty_fields'));
            setDetailsInputWithError(true);
        }

        // Verificar si los datos son válidos y no hay errores de validación
        const isValid = isUserDataValid && isDetailsValid && !newData.error;

        /** Actualizar el estado de validez del formulario */
        setFormIsValid(Boolean(isValid));
    };

    /** Función que envia la cita al back */
    const sendAppointmentForm = async (): Promise<void> => {
        // Validar antes de enviar
        if (!formIsValid) {
            return;
        }

        const appointmentForm: IAppointmentStore = {
            firstName: userData.current.firstName,
            lastName: userData.current.lastName,
            phoneNumber: userData.current.phone,
            emailAccount: userData.current.email,
            details: detailsRef.current?.value ?? '',
            sku: product?.sku ?? '',
            clientId: userLogued.user.id && userLogued.user.id > 0 ? userLogued.user.id : null,
            storeId: storeData.storeId || store.DEFAULT, // Usar el operador || para prevenir undefined/null
        };

        const responseAppointment: boolean = await sendAppointment(appointmentForm);
        if (responseAppointment) {
            userData.current = emptyUserData;
            onClose();
            setSuccessAlertAppointment(true);
        } else {
            setFormIsValid(false);
            setErrorAlertAppointment(true);
        }
    };

    // Validar los campos cuando se monta el componente
    useEffect(() => {
        // Realizar una validación inicial
        if (detailsRef.current) {
            updateClientData(userData.current);
        }
    }, []);

    return (
        <Modal
            fullscreen={isMobile ? true : undefined}
            className={`bg-transparent-black `}
            show={showModal}
            onHide={isBlocked ? undefined : onClose}
            dialogClassName='  modal-dialog-centered '
            contentClassName='rounded-top-4 mh-400-px d-flex justify-content-between shadow '
            size='lg'
        >
            {isMobile && (
                <Col xs={12} className='text-end pe-2 py-2  d-flex justify-content-end'>
                    <CloseIcon classMain='btn-icon-light' onClick={onClose} />
                </Col>
            )}
            <Modal.Body className='p-0 '>
                <Col xs={11} lg={9} className='mx-auto '>
                    <Col xs={12} className={classTitle}>
                        {title}
                    </Col>

                    <Col xs={12} className={classContent}>
                        <Col lg={12} className='text-center'>
                            <p className='text-secondary-2 text-justify paragraph  mt-4'>{BOOK_APPOINTMENT_TEXT}</p>
                        </Col>
                        {product && (
                            <Col lg={12} className='d-flex flex-row ' >
                                <Col lg={2} className='d-flex'>
                                    <Image src={product?.multimedia[0]} width={100}></Image>
                                </Col>
                                <Col lg={10} className='d-flex flex-column  justify-content-center align-items-start ps-3'>
                                    <span className='d-flex text-secondary-1 fw-bold p-large pb-3'>{product?.sku}</span>
                                    <span className='text-start paragraph text-secondary-2'>{product?.title}</span>
                                </Col>
                            </Col>
                        )}

                        <Col className='pb-1'>
                            <UserDataForm
                                withOfficialId={false}
                                clientData={userData.current}
                                updateClientData={updateClientData}
                            />
                        </Col>

                        <Col className='px-1 pb-3'>
                            <InputForm
                                inputRef={detailsRef}
                                iconName=''
                                label={MODAL_DETAILS_APPOINTMENT}
                                type={'textarea'}
                                rowsValue={3}
                                maxLength={4000}
                                classMain={'form-control'}
                                classLabel={'p-regular-medium'}
                                onChange={() => {
                                    updateClientData(userData.current);
                                }}
                                withErrors={detailsInputWithError}
                                messageError={errorMessage}
                            />
                        </Col>
                        <Col className={isMobile ? 'pb-5' : 'pb-3'}>
                            <TwoButtonsGroup
                                classContainer='pt-3 pb-1 px-0'
                                firstButtonClass='btn-secondary-icon-small'
                                firstButtonText={translate('sales.cancel_button_text')}
                                firstButtonClick={onClose}
                                secondButtonClass={`btn-primary-icon-small ${!formIsValid ? 'disabled' : ''}`}
                                secondButtonText={translate('sales.send_button_text')}
                                secondButtonClick={formIsValid ? sendAppointmentForm : undefined}
                            />
                        </Col>
                    </Col>
                </Col>
                <Col xs={12} className='align-self-end pt-2'>
                    <div className={'bg-primary-1 pt-2'} />
                </Col>
            </Modal.Body>
        </Modal>
    );
};

export default BookAppointmentModal;
